import React, { useState, useEffect } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import HeaderNav from '../components/HeaderNav';
import ImageLayout from '../components/ImageLayout';
import Products from '../components/Products';
import AnimatedProductCard from '../components/AnimatedProductCard';
import Loader from '../components/Loader';
import Message from '../components/Message';
import Meta from '../components/Meta';
import StayTop from '../components/StayTop';
import { useGetProductsQuery } from '../slices/productsApiSlice';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import RecentlyViewed from '../components/RecentlyViewed';


const PRODUCTS_PER_LOAD = 24;

const HomeScreen = () => {
    const { keyword } = useParams();
    const [searchParams] = useSearchParams();
    const [description, setDescription] = useState('');
    const [descriptionType, setDescriptionType] = useState('');
    const [visibleCount, setVisibleCount] = useState(PRODUCTS_PER_LOAD);

    // Read filter params from URL
    const productType = searchParams.getAll('productType');
    const minPrice = searchParams.get('minPrice');
    const maxPrice = searchParams.get('maxPrice');
    const inStock = searchParams.get('inStock');
    const onSale = searchParams.get('onSale');
    const color = searchParams.getAll('color');
    const character = searchParams.getAll('character');

    // Fetch products - NO pageNumber means get ALL products
    const { data, isLoading, error } = useGetProductsQuery({
        keyword,
        productType: productType.length > 0 ? productType : undefined,
        minPrice,
        maxPrice,
        inStock,
        onSale,
        color: color.length > 0 ? color : undefined,
        character: character.length > 0 ? character : undefined,
    });

    const { recentlyViewed } = useRecentlyViewed();

    // Function to update the description from HeaderNav
    const updateProductDescription = (newDescription) => {
        setDescription(newDescription ? String(newDescription) : '');
        setDescriptionType('product');
    };

    // Function to update the description from ImageLayout
    const updateKeywordDescription = (newDescription) => {
        setDescription(newDescription ? String(newDescription) : '');
        setDescriptionType('keyword');
    };

    // Clear description when keyword changes
    useEffect(() => {
        if (!keyword) {
            setDescription('');
            setDescriptionType('');
        }
    }, [keyword]);

    // Scroll to top when keyword or filters change
    useEffect(() => {
        window.scrollTo(0, 0);
        setVisibleCount(PRODUCTS_PER_LOAD);
    }, [keyword, searchParams.toString()]);

    return (
        <>
            <HeaderNav onProductSelect={updateProductDescription} />
            {isLoading ? (
                <Loader />
            ) : error ? (
                <Message variant='danger'>{error?.data?.message || error.error}</Message>
            ) : (
                <>
                    <ImageLayout onIconClick={updateKeywordDescription} />
                    <Meta />

                    {descriptionType === 'keyword' && description && typeof description === 'string' && (
                        <p className='d-flex justify-content-center mb-4 mt-4 sexy-text2'>
                            {description}
                        </p>
                    )}

                    {descriptionType === 'product' && description && typeof description === 'string' && (
                        <h3 className='d-flex justify-content-center mb-4 mt-4 description-style sexy-text2'>
                            {description}
                        </h3>
                    )}

                    {/* Show count of products above the grid */}
                    {data.products.length > 0 && (
                        <div style={{
                            textAlign: 'center',
                            margin: '1rem 0 2rem 0',
                            color: '#888',
                            fontSize: '0.95rem',
                            fontWeight: '500'
                        }}>
                            {data.products.length} product{data.products.length !== 1 ? 's' : ''} found
                        </div>
                    )}

                    <Row id="targetRow">
                        {data.products.slice(0, visibleCount).map((product, index) => (
                            <Col key={product._id} xs={6} sm={6} md={6} lg={3} xl={3}>
                                <AnimatedProductCard index={index % 4}>
                                    <Products product={product} showDiscount={product.isClearance} />
                                </AnimatedProductCard>
                            </Col>
                        ))}
                    </Row>

                    {visibleCount < data.products.length && (
                        <div style={{ textAlign: 'center', margin: '2rem 0' }}>
                            <p style={{ color: '#666', fontSize: '0.9rem' }}>
                                Showing {visibleCount} of {data.products.length} products
                            </p>
                            <Button
                                className="blue-outline"
                                style={{ minHeight: '48px', padding: '0 2rem', borderRadius: '50px' }}
                                onClick={() => setVisibleCount((count) => count + PRODUCTS_PER_LOAD)}
                            >
                                Load More
                            </Button>
                        </div>
                    )}

                    {/* Show count of products at bottom - confirms end */}
                    {data.products.length > 0 && visibleCount >= data.products.length && (
                        <div style={{
                            textAlign: 'center',
                            margin: '2rem 0',
                            color: '#666',
                            fontSize: '0.9rem'
                        }}>
                            You've seen all {data.products.length} product{data.products.length !== 1 ? 's' : ''} 🎮
                        </div>
                    )}
                </>
            )}
            <StayTop />
            <div style={{ marginTop: '8rem' }}>
                <RecentlyViewed products={recentlyViewed.slice(0, 8)} />
            </div>
        </>
    );
};

export default HomeScreen;